/**
 * Soft scheduling (design spec §9.5): `want` of a recurring task. Mirror of `server/drang/core/want.py`;
 * both pass `tests/vectors/want.json`.
 *
 *   age  = (now - lastDoneAt) / 1 day        (lastDoneAt falls back to createdAt)
 *   want = 0                       if age < minGapDays
 *        = (age / periodDays) ^ elasticity   otherwise
 */
export const DAY_MS = 86_400_000;
export const DEFAULT_PERIOD_DAYS = 7;
export const DEFAULT_ELASTICITY = 1;
export const DEFAULT_MIN_GAP_DAYS = 0;
export const DEFAULT_CEILING = 3;
export const MIN_ELASTICITY = 0.5;
export const MAX_ELASTICITY = 3;

export type WantLevel = "low" | "mid" | "high" | "max";

export interface WantInputs {
  periodDays: number | null | undefined;
  lastDoneAt: string | null | undefined;
  createdAt: string;
  elasticity?: number | null;
  minGapDays?: number | null;
  ceiling?: number | null;
}

const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);

export function ageDays(inputs: WantInputs, nowMs: number): number {
  const anchor = inputs.lastDoneAt || inputs.createdAt;
  const age = (nowMs - Date.parse(anchor)) / DAY_MS;
  return age > 0 ? age : 0;
}

export function want(inputs: WantInputs, nowMs: number): number {
  const period = inputs.periodDays && inputs.periodDays > 0 ? inputs.periodDays : DEFAULT_PERIOD_DAYS;
  const elasticity = clamp(inputs.elasticity ?? DEFAULT_ELASTICITY, MIN_ELASTICITY, MAX_ELASTICITY);
  const minGap = inputs.minGapDays ?? DEFAULT_MIN_GAP_DAYS;
  const age = ageDays(inputs, nowMs);
  if (age < minGap) return 0;
  return Math.pow(age / period, elasticity);
}

/** Colour band for the bar: neutral → accent → warm → hot. */
export function wantLevel(w: number): WantLevel {
  if (w < 0.5) return "low";
  if (w < 1) return "mid";
  if (w < 2) return "high";
  return "max";
}

/** Clamped for the bar; the raw value stays available for sorting and "why?". */
export function wantDisplay(w: number, ceiling?: number | null): number {
  const c = ceiling && ceiling > 0 ? ceiling : DEFAULT_CEILING;
  return clamp(w, 0, c);
}

export function round6(x: number): number {
  return Math.round(x * 1e6) / 1e6;
}
